import { Button } from "@/components/ui/button.tsx";
import { useToast } from "@/components/ui/use-toast";
import { useCreateEscalation } from "@/lib/api/escalations.ts";
import { MentorshipDetails } from "@/lib/api/mentorships";
import { cn } from "@/lib/utils";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { Loader2 } from "lucide-react";
import { useState } from "react";

interface EscalationSeverityConfirmationProps {
  mentorship: MentorshipDetails;
  onClose: () => void;
  onThreatConfirm: () => void;
  onThreatCancel: () => void;
}

export function EscalationSeverityConfirmation({
  mentorship,
  onClose,
  onThreatConfirm,
  onThreatCancel,
}: EscalationSeverityConfirmationProps) {
  const { toast } = useToast();
  const { mentee } = mentorship || {};
  const [threat, setThreat] = useState(false);
  const { mutateAsync: createEscalation, isPending } = useCreateEscalation(
    mentee?.id || "",
  );

  const handleCreateEscalation = async (threat: boolean) => {
    try {
      setThreat(threat);
      await createEscalation({ threat, mentorshipId: mentorship.id });
      if (threat) {
        onThreatConfirm();
      } else {
        onThreatCancel();
      }

      toast({
        title: "Successfully started Escalation",
        description:
          "Supervisor has been notified and will be messaging you shortly.",
        className: cn(
          "top-0 right-0 flex fixed md:max-w-[420px] md:top-4 md:right-4",
          // "border-2 border-primary shadow-lg rounded-lg",
          // "bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
        ),
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div className="mb-6 mt-4 inline-flex h-20 items-center justify-center gap-[11.11px] rounded-full bg-[#ffedea] p-[17.78px]">
        <div className="flex h-[44.44px] w-[44.44px] items-center justify-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-600" />
        </div>
      </div>

      <div className="space-y-4 text-center">
        <h2 className="text-2xl font-bold leading-tight">
          Is your teen an immediate threat to themselves or someone else?
        </h2>
        <p className="text-base text-slate-600">
          If so, please click continue to view emergency contact info for the
          teen and call 911.
        </p>
      </div>

      <div className="mt-8 flex w-full max-w-lg gap-3">
        <Button
          variant="outline"
          size="lg"
          className="w-full rounded-xl border-2 border-slate-200 bg-white px-6 py-6 text-xl font-bold text-slate-900 hover:bg-slate-50"
          onClick={() => handleCreateEscalation(false)}
          disabled={isPending}
        >
          {isPending && !threat ? (
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          ) : null}
          No
        </Button>
        <Button
          variant="destructive"
          size="lg"
          className="w-full rounded-xl bg-red-600 px-6 py-6 text-xl font-bold hover:bg-red-700"
          onClick={() => handleCreateEscalation(true)}
          disabled={isPending}
        >
          {isPending && threat ? (
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          ) : null}
          Yes
        </Button>
      </div>

      <button
        onClick={onClose}
        className="mt-2 text-lg text-gray-400 underline hover:text-slate-700 hover:underline"
      >
        cancel
      </button>
    </div>
  );
}
